import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Camera, RefreshCw, RotateCcw, SendHorizontal, X } from "lucide-react";

const constraints = (facing) => ({
  audio: false,
  video: { facingMode: { ideal: facing }, width: { ideal: 1920 }, height: { ideal: 1080 } },
});

/** In-chat camera (WhatsApp-like). onCapture(File) sends the photo; onFallback opens the phone's own camera. */
export const CameraSheet = ({ open, onClose, onCapture, onFallback }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [facing, setFacing] = useState("environment");
  const [shot, setShot] = useState(null);
  const [failed, setFailed] = useState(false);

  const stop = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  useEffect(() => {
    if (!open || shot) return undefined;
    let alive = true;
    setFailed(false);
    if (!navigator.mediaDevices?.getUserMedia) { setFailed(true); return undefined; }
    navigator.mediaDevices.getUserMedia(constraints(facing))
      .then((stream) => {
        if (!alive) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) { videoRef.current.srcObject = stream; videoRef.current.play().catch(() => {}); }
      })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; stop(); };
  }, [open, facing, shot]);

  useEffect(() => {
    if (open) return undefined;
    setShot(null);
    return undefined;
  }, [open]);

  useEffect(() => () => { if (shot?.url) URL.revokeObjectURL(shot.url); }, [shot]);

  if (!open) return null;

  const take = () => {
    const v = videoRef.current;
    if (!v?.videoWidth) return;
    const c = document.createElement("canvas");
    c.width = v.videoWidth;
    c.height = v.videoHeight;
    c.getContext("2d").drawImage(v, 0, 0);
    c.toBlob((blob) => {
      if (!blob) return;
      stop();
      setShot({ blob, url: URL.createObjectURL(blob) });
    }, "image/jpeg", 0.88);
  };

  const send = () => {
    onCapture(new File([shot.blob], `foto-${Date.now()}.jpg`, { type: "image/jpeg" }));
    onClose();
  };

  return createPortal(
    <div className="cam-sheet" data-testid="camera-sheet" role="dialog" aria-label="Câmera">
      <div className="cam-top">
        <button type="button" className="cam-icon" data-testid="camera-close" aria-label="Fechar câmera" onClick={() => { stop(); onClose(); }}>
          <X size={24} />
        </button>
        {!shot && !failed && <p className="cam-hint">Aproxime até o código (MAC) ficar legível</p>}
      </div>

      <div className="cam-view">
        {shot ? <img src={shot.url} alt="Foto tirada" className="cam-media" data-testid="camera-preview" />
          : failed ? (
            <div className="cam-failed" data-testid="camera-failed">
              <Camera size={40} />
              <p>Não foi possível abrir a câmera aqui dentro.</p>
              <button type="button" className="cam-pill" data-testid="camera-fallback" onClick={() => { onClose(); onFallback(); }}>
                Usar a câmera do celular
              </button>
            </div>
          ) : <video ref={videoRef} className="cam-media" autoPlay playsInline muted data-testid="camera-video" />}
      </div>

      <div className="cam-bar">
        {shot ? (
          <>
            <button type="button" className="cam-pill cam-ghost" data-testid="camera-retake" onClick={() => setShot(null)}>
              <RotateCcw size={18} /> Tirar outra
            </button>
            <button type="button" className="cam-send" data-testid="camera-send" aria-label="Enviar foto" onClick={send}>
              <SendHorizontal size={24} />
            </button>
          </>
        ) : !failed && (
          <>
            <span className="cam-spacer" />
            <button type="button" className="cam-shutter" data-testid="camera-shutter" aria-label="Tirar foto" onClick={take} />
            <button type="button" className="cam-icon" data-testid="camera-switch" aria-label="Trocar câmera"
              onClick={() => setFacing((f) => (f === "environment" ? "user" : "environment"))}>
              <RefreshCw size={22} />
            </button>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
};
